/**
 * 부서 화면구성용 프로그램 카탈로그
 * — hrMenuConfig 메뉴 트리를 그대로 펼쳐 상위 체크 시 하위 일괄 선택에 사용
 */
import { HR_LEFT_MENUS, HR_TOP_MENUS, MENU_TYPE_LABEL } from 'config/hrMenuConfig';

const TOP_TO_MODULE = {
  '100': 'HR',
  '200': 'BASE',
  '300': 'BUD',
};

function inferModuleCode(screenId, topMenuId) {
  const sid = String(screenId || '');
  if (sid.startsWith('HR_')) return 'HR';
  if (sid.startsWith('BUD_')) return 'BUD';
  if (sid.startsWith('ADM_')) return 'BASE';
  return TOP_TO_MODULE[topMenuId] || 'BASE';
}

/** Sidebar와 동일 — 대메뉴와 라벨이 같은 래퍼 1단은 풀어 하위만 사용 */
function unwrapTopChildren(topId, trees) {
  if (
    trees?.length === 1 &&
    trees[0]?.children?.length &&
    (trees[0].id === topId || trees[0].label === HR_TOP_MENUS.find((t) => t.id === topId)?.label)
  ) {
    return trees[0].children;
  }
  return trees || [];
}

function buildMenuNode(node, ctx) {
  if (!node) return null;
  const pathLabels = [...ctx.pathLabels, node.label].filter(Boolean);

  if (node.children?.length) {
    const subRows = node.children
      .map((c) => buildMenuNode(c, { ...ctx, pathLabels }))
      .filter(Boolean);
    if (!subRows.length) return null;
    return {
      id: `menu-${node.id || node.menuNo || node.label}`,
      nodeType: 'folder',
      menuName: node.label || '',
      menuNo: node.menuNo || node.id || '',
      menuPath: pathLabels.join(' > '),
      moduleCode: ctx.moduleCode,
      moduleName: ctx.moduleName,
      grantedYn: 'N',
      screenId: '',
      menuTypeNm: '',
      subRows,
    };
  }

  const screenId = node.screenId || node.progId;
  if (!screenId) return null;
  const moduleCode = inferModuleCode(screenId, ctx.topMenuId);
  return {
    id: `screen-${screenId}`,
    nodeType: 'screen',
    menuName: node.label || screenId,
    screenId,
    progId: node.progId || screenId,
    menuNo: node.menuNo || node.id || '',
    menuPath: pathLabels.join(' > '),
    menuType: node.menuType || '',
    menuTypeNm: MENU_TYPE_LABEL[node.menuType] || node.menuType || '',
    moduleCode,
    moduleName: ctx.moduleName,
    grantedYn: 'N',
    adminOnly: !!node.adminOnly,
    masterOnly: !!node.masterOnly,
  };
}

let cachedTree = null;
let cachedFlat = null;

/** 대메뉴(모듈) 루트 → 하위 메뉴/화면 트리 */
export function buildDeptAuthProgramTree() {
  if (cachedTree) return cachedTree;
  const roots = (HR_TOP_MENUS || []).map((top) => {
    const moduleCode = TOP_TO_MODULE[top.id] || 'BASE';
    const children = unwrapTopChildren(top.id, HR_LEFT_MENUS[top.id]).map((n) =>
      buildMenuNode(n, {
        topMenuId: top.id,
        moduleCode,
        moduleName: top.label,
        pathLabels: [top.label],
      })
    ).filter(Boolean);
    return {
      id: `mod-${top.id}`,
      nodeType: 'module',
      menuName: top.label,
      menuNo: top.menuNo || top.id,
      menuPath: top.label,
      moduleCode,
      moduleName: top.label,
      grantedYn: 'N',
      screenId: '',
      menuTypeNm: '',
      subRows: children,
    };
  });
  cachedTree = roots;
  return roots;
}

/** leaf 화면만 flat (검증·카운트용) */
export function listDeptAuthPrograms() {
  if (cachedFlat) return cachedFlat;
  const out = [];
  const walk = (nodes) => {
    (nodes || []).forEach((n) => {
      if (n.subRows?.length) walk(n.subRows);
      else if (n.screenId) {
        out.push({
          screenId: n.screenId,
          progId: n.progId || n.screenId,
          screenName: n.menuName,
          moduleCode: n.moduleCode,
          moduleName: n.moduleName,
          menuType: n.menuType,
          menuTypeNm: n.menuTypeNm,
          menuPath: n.menuPath,
          menuNo: n.menuNo,
        });
      }
    });
  };
  walk(buildDeptAuthProgramTree());
  cachedFlat = out;
  return out;
}

export function countProgramsByModule() {
  const counts = {};
  listDeptAuthPrograms().forEach((p) => {
    counts[p.moduleCode] = (counts[p.moduleCode] || 0) + 1;
  });
  return counts;
}
